import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash-es";

import Footer from "@/components/shared/Footer";
import { VendorUtilities, apiFetchVendorV1 } from "@/utilities/VendorUtilities";
import { SessionManager } from "@/services/SessionManager";
import SwitchInput from "@/components/shared/SwitchInput";
import { SET_SESSION_DATA } from "@/redux/auth/sessions/actions";
import Alert from "../shared/Alert";
import { Button } from "../shared/Button";
import { Icon } from "../shared/Icon";
import PasswordInput from "../shared/PasswordInput";
import TextInput from "../shared/TextInput";

const CreateTeam = ({ err, clearServerError, setLoadingToFalse }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [autojoinEnabled, setAutojoinEnabled] = useState(true);
  const [errors, setErrors] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.sessions.sessionData.user);

  const { mutate: registerTeam, isLoading: registerTeamLoading } = useMutation({
    mutationFn: async (payload: any) => {
      const path = "/signup/register";
      return (
        await apiFetchVendorV1(path, {
          endpoint: SessionManager.getApiEndpoint(),
          method: "POST",
          body: JSON.stringify(payload)
        })
      ).json();
    },
    retry: false,
    onError: async (err: any) => {
      setErrors(JSON.parse(err?.message));
    },
    onSuccess: async data => {
      const parsedData = data;
      dispatch({ type: SET_SESSION_DATA, payload: parsedData });
      SessionManager.setSessionId();
      navigate("/new-application");
    }
  });

  const handleFormChange = (name: string, value: string) => {
    switch (name) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "company":
        setCompany(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      default:
        return;
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (!isEmpty(errors)) {
      setErrors(null);
    }
    if (err) {
      clearServerError();
    }

    const canSubmit = !registerTeamLoading && validatePasswordFields();

    if (canSubmit) {
      registerTeam({
        password: password.trim() || "",
        first_name: firstName.trim() || "",
        last_name: lastName.trim() || "",
        company: company.trim() || "",
        team_autojoin_enabled: user?.has_private_email_domain ? autojoinEnabled : false
      });
    }
  };

  useEffect(() => {
    VendorUtilities.purgeVOneData();
    setLoadingToFalse();
  }, []);

  const validatePasswordFields = () => {
    if (password !== confirmPassword) {
      setErrors({
        ...errors,
        password: "Passwords do not match"
      });
      return false;
    }
    if (password.length > 72) {
      setErrors({
        ...errors,
        password: "Password cannot be longer than 72 characters"
      });
      return false;
    }
    return true;
  };

  const handleAutojoinToggle = async () => {
    setAutojoinEnabled(!autojoinEnabled);
  };

  return (
    <div className="flex flex-col w-full bg-color--teal justify-center items-center h-full">
      <BasicNavBar />
      <div className="py-10 px-4 box-border h-full w-full flex flex-col flex-1 min-h-0 items-center justify-center bg-color--teal overflow-y-auto">
        <div className="flex flex-col min-[800px]:flex-row flex-none w-full lg:my-10 CreateApp-wrapper">
          <div className="illustration-wrapper hidden lg:flex lg:flex-1 lg:flex-col lg:justify-center">
            <div className="left-content-wrap flex flex-col justify-center items-center">
              <span className="icon u-createTeam"></span>
            </div>
          </div>
          <div className="flex flex-col flex-1 items-center lg:items-start">
            <div className="flex flex-col flex-1 gap-8 right-content-wrap box-border">
              <div>
                <h1 className="text-indigo-500 leading-[1.4]">
                  Create your team
                </h1>
                <p className="text-base font-normal text-gray-600 leading-[1.4] mt-3">
                  Before we get started, let's get some information about you and your
                  team.
                </p>
              </div>
              {/* Catchall for all other server errors when registering */}
              {errors && errors?.validateError && (
                <Alert variant="error" className="!m-0 !p-4">
                  {Object.keys(errors).map(key =>
                    key === "validateError" ? null : (
                      <p key={key}>{errors[key].error}</p>
                    )
                  )}
                </Alert>
              )}
              <div className="CreateAppPlatform--formWrapper">
                <div className="auth-pages flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {!errors?.validateError && errors?.firstName && (
                        <Alert variant="error" className="!m-0 !p-4">
                          {errors?.firstName}
                        </Alert>
                      )}
                      <label htmlFor="firstName">First Name</label>
                      <TextInput
                        type="text"
                        placeholder="Jack"
                        size={20}
                        value={firstName}
                        name="firstName"
                        onChangeValue={value => handleFormChange("firstName", value)}
                        ariaLabel="First Name"
                      />
                    </div>
                    <div>
                      {!errors?.validateError && errors?.lastName && (
                        <Alert variant="error" className="!m-0 !p-4">
                          {errors?.lastName}
                        </Alert>
                      )}
                      <label htmlFor="lastName">Last Name</label>
                      <TextInput
                        type="text"
                        placeholder="Shephard"
                        size={20}
                        value={lastName}
                        name="lastName"
                        onChangeValue={value => handleFormChange("lastName", value)}
                        ariaLabel="Last Name"
                      />
                    </div>
                  </div>
                  <div>
                    {!errors?.validateError && errors?.company && (
                      <Alert variant="error" className="!m-0 !p-4">
                        {errors?.company}
                      </Alert>
                    )}
                    <label htmlFor="company">Company</label>
                    <TextInput
                      type="text"
                      placeholder="Company Name"
                      value={company}
                      name="company"
                      onChangeValue={value => handleFormChange("company", value)}
                      ariaLabel="Company Name"
                    />
                  </div>
                  {!errors?.validateError && errors?.password && (
                    <Alert variant="error" className="!m-0 !p-4">
                      {errors?.password}
                    </Alert>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="password">Password</label>
                      <PasswordInput
                        name="password"
                        className="Input"
                        placeholder="Password"
                        autoComplete="new-password"
                        value={password}
                        onChangeValue={setPassword}
                      />
                    </div>
                    <div>
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <PasswordInput
                        name="confirmPassword"
                        className="Input"
                        placeholder="Confirm Password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChangeValue={setConfirmPassword}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {user?.has_private_email_domain && (
              <div className="mt-4 flex flex-col gap-2 right-content-wrap box-border">
                <SwitchInput
                  name="create-team-autojoin"
                  label="Enable Team Auto-join"
                  checked={autojoinEnabled}
                  onChange={handleAutojoinToggle}
                />
                <p className="text-gray-600 text-sm">
                  When team auto-join is enabled, anyone with a validated{" "}
                  <span className="font-semibold">{user?.domain}</span> email will be
                  able to join this team. The default policy assigned is{" "}
                  <span className="font-semibold">Read Only</span> - you can change
                  the default on the{" "}
                  <Link
                    target="_blank"
                    to="/team/autojoin"
                    className="text-blue-300 hover:underline font-semibold"
                  >
                    Auto-join settings page
                  </Link>{" "}
                  after you create your team.
                </p>
              </div>
            )}
            <div className="flex flex-col right-content-wrap box-border">
              <div className="FormButton-wrapper flex mt-8">
                <Button kind="primary-purple" size="large" onClick={onSubmit}>
                  {registerTeamLoading ? "Loading..." : "Continue"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer>
        <Footer />
      </footer>
    </div>
  );
};

const BasicNavBar = () => (
  <header className="w-full flex flex-col">
    <div className="w-full relative bg-white shadow-[0_1px_4px_rgba(0,0,0,0.16)] z-[400] px-5 py-3 box-border flex flex-1 justify-start items-center">
      <div className="mr-5 relative">
        <Link to="/" className="flex flex-row" aria-label="Replicated home">
          <Icon name="replicatedLogo" width="150" height="34" />
        </Link>
      </div>
    </div>
  </header>
);

export default CreateTeam;
